import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "progress", "progressBar", "phaseName", "factsCount"]
  static values = {
    bundleId: String,
    companySlug: String,
    pollingInterval: { type: Number, default: 3000 }
  }

  connect() {
    // Start polling if bundle is in progress
    if (this.shouldPoll()) {
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  shouldPoll() {
    const status = this.element.dataset.bundleStatus
    return status === "pending" || status === "researching"
  }

  startPolling() {
    if (this.pollingTimer) {
      return // Already polling
    }

    this.pollingTimer = setInterval(() => {
      this.checkStatus()
    }, this.pollingIntervalValue)
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  }

  async checkStatus() {
    try {
      const url = this.companySlugValue
        ? `/companies/${this.companySlugValue}/prospecting_bundles/${this.bundleIdValue}/status`
        : `/prospecting_bundles/${this.bundleIdValue}/status`

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.updateStatus(data)

      // Stop polling if completed or failed
      if (data.status === "completed" || data.status === "failed") {
        this.stopPolling()
        
        // Reload page after a short delay to show final results
        if (data.status === "completed") {
          setTimeout(() => {
            window.location.reload()
          }, 1000)
        }
      }
    } catch (error) {
      console.error("Error checking bundle status:", error)
      // Don't stop polling on error, just log it
    }
  }

  updateStatus(data) {
    // Update status badge
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.humanize(data.status)
      this.statusTarget.className = this.getStatusClass(data.status)
    }

    // Update progress
    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `${data.progress}%`
    }

    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${data.progress}%`
    }

    // Update phase name
    if (this.hasPhaseNameTarget) {
      this.phaseNameTarget.textContent = data.phase_name || "Not started"
    }

    // Update facts count
    if (this.hasFactsCountTarget) {
      this.factsCountTarget.textContent = `${data.facts_count} facts extracted`
    }

    // Update element dataset for shouldPoll check
    this.element.dataset.bundleStatus = data.status
  }

  getStatusClass(status) {
    const baseClasses = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
    const statusClasses = {
      "pending": "bg-gray-100 text-gray-800",
      "researching": "bg-blue-100 text-blue-800",
      "completed": "bg-green-100 text-green-800",
      "failed": "bg-red-100 text-red-800",
      "reviewing": "bg-yellow-100 text-yellow-800"
    }
    return `${baseClasses} ${statusClasses[status] || "bg-gray-100 text-gray-800"}`
  }

  humanize(str) {
    if (!str) return ""
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, l => l.toUpperCase())
  }
}

